import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Pagination
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Devices as DeviceIcon,
  Person as PersonIcon,
  NextPlan as NextStepIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import StatusChip from '../shared/StatusChip';
import UrgencyChip from '../shared/UrgencyChip';
import SupportTypeChip from '../shared/SupportTypeChip';
import { formatDate, getTimeDifference } from '../../utils/ticketTransformers';

/**
 * Mobile-friendly card view for tickets
 */
const TicketMobileCards = ({
  tickets,
  totalTickets,
  page,
  rowsPerPage,
  locations = [],
  onPageChange,
  onViewTicket,
  onEditTicket
}) => {
  const { t } = useThemeSettings();
  const { hasPermission } = usePermissions();

  const totalPages = Math.ceil(totalTickets / rowsPerPage);

  const handlePageChange = (event, newPage) => {
    onPageChange(event, newPage - 1); // Convert to 0-based
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tickets.map((ticket) => (
        <Paper
          key={ticket.id}
          sx={{
            p: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => onViewTicket(ticket)}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              #{ticket.id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <StatusChip status={ticket.status} size="small" />
              <UrgencyChip urgency={ticket.urgency} size="small" />
            </Box>
          </Box>

          {/* Title */}
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            {ticket.title || ticket.description?.substring(0, 60)}
            {ticket.description?.length > 60 && '...'}
          </Typography>

          {/* Support Type */}
          <Box sx={{ mb: 1 }}>
            <SupportTypeChip type={ticket.support_type} size="small" />
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Next Step, Location, Device & Creator */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
            {ticket.next_step && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NextStepIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                  {ticket.next_step}
                </Typography>
              </Box>
            )}
            {(() => {
              const locationId = ticket.locationId || ticket.location_id || ticket.location?.id;

              // Try to find location from locations array
              const foundLocation = locations?.find(l =>
                l.id === locationId ||
                l.id === parseInt(locationId) ||
                String(l.id) === String(locationId)
              );

              // Get friendlyName from ticket, but check if it's a real name or just a fallback
              const ticketLocationName = ticket.location?.friendlyName || ticket.location?.name;
              const isFallbackName = ticketLocationName && ticketLocationName.startsWith('Location ');

              // Prefer the lookup result, fall back to ticket data only if lookup fails and it's not a fallback
              const locationName = foundLocation?.friendlyName ||
                (!isFallbackName ? ticketLocationName : null);

              return locationName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {locationName}
                  </Typography>
                </Box>
              ) : null;
            })()}
            {(ticket.related_device_name || ticket.device?.friendlyName) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DeviceIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {ticket.related_device_name || ticket.device?.friendlyName}
                </Typography>
              </Box>
            )}
            {ticket.createdByUser?.name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {ticket.createdByUser.name}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {getTimeDifference(ticket.createdAt)}
            </Typography>
            <Box onClick={(e) => e.stopPropagation()}>
              <IconButton size="small" onClick={() => onViewTicket(ticket)}>
                <ViewIcon fontSize="small" />
              </IconButton>
              {hasPermission('tickets.edit') && (
                <IconButton size="small" onClick={() => onEditTicket(ticket)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Paper>
      ))}

      {tickets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t ? t('Tickets.no_tickets') : 'No tickets found'}
          </Typography>
        </Paper>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default TicketMobileCards;
